import {
  Activity,
  CheckCircle2,
  CircleSlash,
  HeartCrack,
  HeartPulse,
  Hourglass,
  PackagePlus,
  Pencil,
  PlugZap,
  RefreshCw,
  Rocket,
  ShieldAlert,
  Trash2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import type { EventType } from '@/types/fleet';

const EVENT_ICONS: Record<EventType, { Icon: LucideIcon; className: string }> = {
  provisioned: { Icon: PackagePlus, className: 'text-blue-600' },
  deployed: { Icon: Rocket, className: 'text-blue-600' },
  updated: { Icon: Pencil, className: 'text-slate-600' },
  restarted: { Icon: RefreshCw, className: 'text-slate-600' },
  quarantined: { Icon: ShieldAlert, className: 'text-amber-600' },
  blocked: { Icon: CircleSlash, className: 'text-red-600' },
  recovered: { Icon: HeartPulse, className: 'text-emerald-600' },
  decommissioned: { Icon: Trash2, className: 'text-slate-500' },
  heartbeat_lost: { Icon: HeartCrack, className: 'text-red-600' },
  heartbeat_resumed: { Icon: PlugZap, className: 'text-emerald-600' },
};

/**
 * A `deployed` event's `payload.provisioning` outcome, written by the API's
 * provision reconciler (OBV-0058). A dispatch keeps the plain deployed icon.
 */
const PROVISION_OUTCOME_ICONS: Record<string, { Icon: LucideIcon; className: string }> = {
  succeeded: { Icon: CheckCircle2, className: 'text-emerald-600' },
  failed: { Icon: XCircle, className: 'text-red-600' },
  abandoned: { Icon: Hourglass, className: 'text-amber-600' },
};

export function EventIcon({
  eventType,
  provisioning,
  className,
}: {
  eventType: EventType;
  /** `payload.provisioning` of a `deployed` event, when it has one. */
  provisioning?: string;
  className?: string;
}): JSX.Element {
  const outcome =
    eventType === 'deployed' && provisioning ? PROVISION_OUTCOME_ICONS[provisioning] : undefined;
  const entry = outcome ??
    EVENT_ICONS[eventType] ?? { Icon: Activity, className: 'text-slate-500' };
  const { Icon } = entry;

  return <Icon className={cn('h-4 w-4', entry.className, className)} aria-hidden />;
}
